/**
 * Calculate proportional distribution of assets across clusters
 * Ensures every cluster with at least 1 asset gets representation
 *
 * Formula: clusterSampleSize = (clusterCount / totalCount) * maxDisplay
 * With minimum of 1 asset per non-empty cluster
 */

export interface ClusterDistribution {
  clusterName: string;
  totalCount: number;
  targetSampleSize: number;
  percentage: number;
}

export function calculateProportionalDistribution(
  clusterCounts: Record<string, number>,
  maxDisplay: number = 1000
): ClusterDistribution[] {
  // Filter out empty clusters
  const nonEmptyClusters = Object.entries(clusterCounts).filter(
    ([_, count]) => count > 0
  );

  if (nonEmptyClusters.length === 0) {
    return [];
  }

  // Calculate total assets across all clusters
  const totalAssets = nonEmptyClusters.reduce(
    (sum, [_, count]) => sum + count,
    0
  );

  // If total assets is less than max display, just show all
  if (totalAssets <= maxDisplay) {
    return nonEmptyClusters.map(([name, count]) => ({
      clusterName: name,
      totalCount: count,
      targetSampleSize: count,
      percentage: (count / totalAssets) * 100,
    }));
  }

  // Calculate proportional distribution
  let distributions: ClusterDistribution[] = [];
  let allocatedSamples = 0;

  // First pass: calculate proportional samples with minimum of 1
  nonEmptyClusters.forEach(([name, count]) => {
    const proportion = count / totalAssets;
    const idealSampleSize = Math.floor(proportion * maxDisplay);
    const sampleSize = Math.max(1, idealSampleSize);

    distributions.push({
      clusterName: name,
      totalCount: count,
      targetSampleSize: sampleSize,
      percentage: proportion * 100,
    });

    allocatedSamples += sampleSize;
  });

  // Second pass: if we're over the limit, proportionally reduce larger clusters
  if (allocatedSamples > maxDisplay) {
    const excess = allocatedSamples - maxDisplay;

    // Sort by sample size descending (create new array to avoid mutation)
    const sortedDistributions = [...distributions].sort(
      (a, b) => b.targetSampleSize - a.targetSampleSize
    );

    // Remove excess from largest clusters first, but keep minimum of 1
    let remaining = excess;
    for (const dist of sortedDistributions) {
      if (remaining <= 0) break;
      if (dist.targetSampleSize > 1) {
        const reduction = Math.min(remaining, dist.targetSampleSize - 1);
        dist.targetSampleSize -= reduction;
        remaining -= reduction;
      }
    }
  }

  // Third pass: if we have room, add to largest clusters proportionally
  else if (allocatedSamples < maxDisplay) {
    const available = maxDisplay - allocatedSamples;

    // Sort by total count descending (create new array to avoid mutation)
    const sortedDistributions = [...distributions].sort(
      (a, b) => b.totalCount - a.totalCount
    );

    // Distribute available slots proportionally to larger clusters
    let remaining = available;
    for (const dist of sortedDistributions) {
      if (remaining <= 0) break;
      const proportion = dist.totalCount / totalAssets;
      const additionalSlots = Math.min(
        remaining,
        Math.floor(proportion * available)
      );
      dist.targetSampleSize += additionalSlots;
      remaining -= additionalSlots;
    }

    // If still have remaining, give to largest cluster
    if (remaining > 0) {
      sortedDistributions[0].targetSampleSize += remaining;
    }
  }

  // Sort alphabetically for consistent display
  distributions.sort((a, b) => a.clusterName.localeCompare(b.clusterName));

  return distributions;
}

/**
 * Calculate which assets to display based on proportional distribution
 * Returns asset keys to include in the graph
 */
export function selectAssetsForDistribution(
  assets: Array<{
    key: string;
    asname?: string;
    asnumber?: string;
    metadata?: any;
  }>,
  clusterField: 'asname' | 'asnumber',
  distributions: ClusterDistribution[]
): string[] {
  const selectedKeys: string[] = [];
  const clusterAssetsMap: Record<
    string,
    Array<{ key: string; index: number }>
  > = {};

  // Group assets by cluster
  assets.forEach((asset, index) => {
    const clusterValue =
      clusterField === 'asname'
        ? asset.asname || asset.metadata?.asname
        : asset.asnumber || asset.metadata?.asnumber;

    if (clusterValue && clusterValue !== '' && clusterValue !== 'null') {
      const clusterStr = String(clusterValue);
      if (!clusterAssetsMap[clusterStr]) {
        clusterAssetsMap[clusterStr] = [];
      }
      clusterAssetsMap[clusterStr].push({ key: asset.key, index });
    }
  });

  // Select assets based on distribution targets
  distributions.forEach(dist => {
    const clusterAssets = clusterAssetsMap[dist.clusterName] || [];
    const samplesToTake = Math.min(dist.targetSampleSize, clusterAssets.length);

    if (samplesToTake > 0) {
      // Randomly sample if we have more assets than needed
      if (clusterAssets.length > samplesToTake) {
        // Shuffle and take first N
        const shuffled = [...clusterAssets].sort(() => Math.random() - 0.5);
        shuffled.slice(0, samplesToTake).forEach(item => {
          selectedKeys.push(item.key);
        });
      } else {
        // Take all assets from this cluster
        clusterAssets.forEach(item => {
          selectedKeys.push(item.key);
        });
      }
    }
  });

  return selectedKeys;
}
